import { Header } from "@/components/Header";
import { LeadForm } from "@/components/LeadForm";
import { Features } from "@/components/Features";
import { Testimonials } from "@/components/Testimonials";
import { Gallery } from "@/components/Gallery";
import { CTA } from "@/components/CTA";
import { Footer } from "@/components/Footer";
import { PaymentEstimator } from "@/components/PaymentEstimator";
import { SEOHead } from "@/components/SEOHead";
import { CheckCircle, ArrowDown } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1763485956294-407e7388f63b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBiYXRocm9vbSUyMHJlbW9kZWwlMjByZW5vdmF0aW9uJTIwbW9kZXJufGVufDB8fHx8MTc3MDMxODAyOXww&ixlib=rb-4.1.0&q=85";

const keyPoints = [
  "Complete Bathroom Demolition & Rebuild",
  "We Install Custom Vanities, Countertops & Cabinets",
  "We Install Premium Tile, Flooring & Wall Finishes",
  "Modern Plumbing & Lighting Upgrades",
  "Steam Shower Systems Available"
];

export default function BathroomRemodeling() {
  return (
    <div className="min-h-screen" data-testid="bathroom-remodeling-page">
      <SEOHead page="bathroom-remodeling" />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        >
          <div className="hero-overlay absolute inset-0" />
        </div>

        <Header />

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-white animate-fade-in">
              <span className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6">
                New Mexico's #1 Bathroom Remodeling Experts
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Playfair_Display'] leading-tight" data-testid="hero-headline">
                Full Bathroom Remodel Services in New Mexico
              </h1>
              <p className="text-xl text-white/90 mb-8 max-w-xl">
                Transform your outdated bathroom into a stunning modern sanctuary. Complete renovation from floor to ceiling in as little as one day.
              </p>

              {/* Key Points */}
              <ul className="space-y-3 mb-8">
                {keyPoints.map((point, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-amber-400 flex-shrink-0" />
                    <span className="text-white/90">{point}</span>
                  </li>
                ))}
              </ul>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 items-center">
                <div className="trust-badge bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <span className="text-sm font-semibold">Licensed & Insured</span>
                </div>
                <div className="trust-badge bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <span className="text-sm font-semibold">Lifetime Warranty</span>
                </div>
                <div className="trust-badge bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <span className="text-sm font-semibold">Free Estimates</span>
                </div>
              </div>
            </div>

            {/* Right - Lead Form + Call CTA */}
            <div className="animate-slide-in-right space-y-6">
              <LeadForm pageType="full-bathroom-remodel" title="Get $1500 Off & Schedule an Appointment" />
              
              {/* Call CTA Section */}
              <div className="glass-form p-6 rounded-2xl shadow-2xl border border-gray-100 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2 font-['Playfair_Display']">
                  Get $2500 Off Your Project When You Call Now
                </h3>
                <p className="text-gray-700 font-medium mb-3">
                  Limited spots available: Schedule Your In-Home Appointment
                </p>
                <a
                  href="tel:+15054727064"
                  className="inline-flex items-center justify-center w-full h-14 bg-[#1E4C9A] hover:bg-[#153975] text-white font-bold text-xl rounded-full shadow-lg hover:shadow-xl transition-all duration-300 mb-2"
                  data-testid="call-cta-button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  +1 (505) 472-7064
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ArrowDown className="h-6 w-6 text-white/70" />
        </div>
      </section>

      {/* Features Section */}
      <Features title="Why Choose Prime Baths for Your Full Remodel?" />

      {/* Payment Estimator */}
      <PaymentEstimator defaultProjectType="bathroom-remodeling" />

      {/* Gallery Section */}
      <Gallery serviceType="bathroom-remodeling" />

      {/* Testimonials Section */}
      <Testimonials />

      {/* CTA Section */}
      <CTA 
        title="Ready for Your Full Bathroom Remodel?"
        subtitle="Get $1500 off your complete bathroom renovation. Schedule your free in-home consultation today."
      />

      {/* Footer */}
      <Footer />
    </div>
  );
}
